/**
 * Native Push Notification Service
 *
 * Uses Capacitor's PushNotifications plugin when running in the native app.
 * In the browser/PWA, this is a no-op (push only works in the native app).
 *
 * Flow:
 *   1. App checks if Capacitor is available (native app)
 *   2. Requests push permission from the OS
 *   3. Receives a device token from APNs (iOS) or FCM (Android)
 *   4. Sends the token to the backend: POST /api/mobile/push/register
 *   5. On logout, sends: POST /api/mobile/push/unregister
 */

const isNative = () => {
  return window.Capacitor?.isNativePlatform?.() || false;
};

/**
 * Obtain the native PushNotifications plugin proxy.
 *
 * This web app is loaded remotely inside the native shell and does NOT bundle
 * `@capacitor/push-notifications`, so `window.Capacitor.Plugins.PushNotifications`
 * is undefined by default (that object is only populated by the plugin's JS
 * `registerPlugin` call). The correct way to reach a compiled-in native plugin
 * from a remote page is `window.Capacitor.registerPlugin(name)`, which creates a
 * bridge proxy bound to the native implementation.
 */
const getPushPlugin = () => {
  const cap = window.Capacitor;
  if (!cap) return null;
  if (cap.Plugins?.PushNotifications) return cap.Plugins.PushNotifications;
  if (typeof cap.registerPlugin === 'function') {
    try { return cap.registerPlugin('PushNotifications'); } catch { return null; }
  }
  return null;
};

/** Whether the push plugin is actually compiled into this native build. */
export function isPushPluginAvailable() {
  const cap = window.Capacitor;
  if (!cap) return false;
  if (typeof cap.isPluginAvailable === 'function') {
    return cap.isPluginAvailable('PushNotifications');
  }
  return !!cap.Plugins?.PushNotifications;
}

/** Runtime diagnostics for on-device debugging (shown in Mobile Settings). */
export function getPushDiagnostics() {
  const cap = window.Capacitor;
  let pluginKeys = [];
  try { pluginKeys = cap?.Plugins ? Object.keys(cap.Plugins) : []; } catch { /* noop */ }
  let headerNames = [];
  try { headerNames = Array.isArray(cap?.PluginHeaders) ? cap.PluginHeaders.map((h) => h.name) : []; } catch { /* noop */ }
  return {
    native: !!cap?.isNativePlatform?.(),
    platform: cap?.getPlatform?.() || 'web',
    hasRegisterPlugin: typeof cap?.registerPlugin === 'function',
    hasIsPluginAvailable: typeof cap?.isPluginAvailable === 'function',
    pluginAvailable: isPushPluginAvailable(),
    inPluginsObject: !!cap?.Plugins?.PushNotifications,
    inPluginHeaders: headerNames.includes('PushNotifications'),
    pushEnabledFlag: PUSH_ENABLED,
    lastToken: _currentToken ? `${_currentToken.slice(0, 10)}…` : null,
    pluginKeys,
  };
}

/**
 * Master switch for native push notifications.
 *
 * Requires the following to be in place in the NATIVE build (not the remote web app):
 *   • mobile-native: `@capacitor/push-notifications` installed + `npx cap sync`
 *   • iOS:     Push Notifications capability enabled in Xcode (direct APNs — no Firebase)
 *   • Android: mobile-native/android/app/google-services.json present (Firebase/FCM)
 *
 * Safe on older/live builds that lack the plugin: registerPushNotifications() early-returns
 * when `window.Capacitor.Plugins.PushNotifications` is undefined, so nothing crashes.
 * On Android WITHOUT google-services.json the gradle google-services plugin isn't applied
 * and the build simply ships without push — it does not crash at runtime.
 */
const PUSH_ENABLED = true;

let _currentToken = null;
let _listenersAttached = false;
let _pendingTap = null;
let _lastRegisterResult = null; // { ok: true } or { ok: false, error: '...' }

/** Get the result of the last registration attempt. */
export function getLastRegisterResult() { return _lastRegisterResult; }

/** Retrieve & clear a push tap that arrived before the UI mounted (cold start). */
export function consumePendingTap() {
  const t = _pendingTap;
  _pendingTap = null;
  return t;
}

/**
 * Register for push notifications.
 * Call this after the user is authenticated.
 * @param {object} api - The authenticated axios instance from MobileAuthContext
 */
export async function registerPushNotifications(api) {
  if (!isNative()) return;
  if (!PUSH_ENABLED) {
    console.debug('[Push] Disabled — Firebase not configured yet. Skipping registration.');
    return;
  }

  try {
    // Obtain the native plugin proxy (via registerPlugin) — the web bundle
    // doesn't import the package, so Capacitor.Plugins.X may be undefined.
    const PushNotifications = getPushPlugin();
    if (!PushNotifications) {
      console.debug('[Push] PushNotifications plugin not available');
      return;
    }

    // 1) Attach listeners FIRST — the `registration` event can fire the instant
    //    register() is called, so the listener must already be in place or the
    //    device token is lost (the classic "no device registered" symptom).
    //    Guard so we don't stack duplicate listeners on repeated calls.
    if (!_listenersAttached) {
      _listenersAttached = true;

      await PushNotifications.addListener('registration', async (token) => {
        console.debug('[Push] Device token received');
        _currentToken = token.value;
        const platform = window.Capacitor.getPlatform(); // 'ios' or 'android'
        try {
          await api.post('/api/mobile/push/register', {
            token: token.value,
            platform: platform,
          });
          console.debug('[Push] Token registered with backend');
          _lastRegisterResult = { ok: true };
        } catch (err) {
          console.error('[Push] Failed to register token with backend:', err);
          const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
          _lastRegisterResult = { ok: false, error: detail };
        }
      });

      await PushNotifications.addListener('registrationError', (error) => {
        console.error('[Push] Registration error:', error);
      });

      await PushNotifications.addListener('pushNotificationReceived', (notification) => {
        console.debug('[Push] Notification received in foreground:', notification.title);
      });

      await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
        console.debug('[Push] Notification tapped:', action.notification?.data);
        const n = action.notification || {};
        const d = n.data || {};
        // Dispatch a global event the mobile app listens for → shows a dialog
        // and offers a deep link to the related job.
        try {
          const detail = {
            title: n.title || d.title || 'Notification',
            body: n.body || d.body || d.message || '',
            jobId: d.job_id || d.jobId || null,
            roomId: d.room_id || null,
            type: d.type || null,
          };
          _pendingTap = detail;
          window.dispatchEvent(new CustomEvent('equipr-push-tap', { detail }));
        } catch (e) { /* noop */ }
      });
    }

    // 2) Android 8+ needs a notification channel for visible pushes.
    if (window.Capacitor.getPlatform() === 'android') {
      try {
        await PushNotifications.createChannel({
          id: 'default',
          name: 'Job notifications',
          description: 'Allocations, job updates and reminders',
          importance: 4,
        });
        await PushNotifications.createChannel({
          id: 'chat',
          name: 'Chat messages',
          description: 'New messages in job and team chats',
          importance: 4,
        });
      } catch (e) {
        console.debug('[Push] createChannel skipped:', e?.message);
      }
    }

    // 3) Request permission (iOS prompt / Android 13+ runtime permission).
    const permResult = await PushNotifications.requestPermissions();
    if (permResult.receive !== 'granted') {
      console.debug('[Push] Permission denied');
      return;
    }

    // 4) Register with the OS — fires the `registration` listener above.
    await PushNotifications.register();

  } catch (err) {
    console.debug('[Push] Not available (expected in browser):', err?.message);
  }
}

/**
 * Unregister the device token from the backend.
 * Call this on logout.
 * @param {object} api - The authenticated axios instance
 */
export async function unregisterPushToken(api) {
  if (!_currentToken || !isNative()) return;

  try {
    await api.post('/api/mobile/push/unregister', { token: _currentToken });
    console.debug('[Push] Token unregistered from backend');
  } catch (err) {
    console.debug('[Push] Failed to unregister token:', err?.message);
  }
  _currentToken = null;
}
